import { AnimatePresence, motion } from 'framer-motion';
import { MessageSquare, Plus, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';

import { ThreadItem } from './ThreadItem';

export interface ThreadListProps {
  threads: Array<{ id: string; title: string }>;
  selectedThreadId: string | null;
  isLoading: boolean;
  onSelectThread: (id: string | null) => void;
  onCreateThread: () => void;
  onDeleteThread?: (id: string) => void;
  onRenameThread?: (id: string, newTitle: string) => void;
  onCopyLink?: (id: string) => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 28,
    },
  },
};

const LoadingSkeleton = () => (
  <div className="space-y-1 p-0.5">
    {[1, 2, 3].map((i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: i * 0.08 }}
        className="h-8 rounded-lg bg-gradient-to-r from-slate-100 to-slate-50 animate-pulse"
      />
    ))}
  </div>
);

const EmptyState = ({ onCreateThread }: { onCreateThread: () => void }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    className="flex flex-col items-center justify-center py-8 px-3"
  >
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.1 }}
      className={cn(
        'w-10 h-10 rounded-xl mb-3',
        'bg-gradient-to-br from-slate-100 to-slate-50',
        'flex items-center justify-center',
        'border border-slate-200/50',
      )}
    >
      <MessageSquare className="w-4 h-4 text-slate-400" />
    </motion.div>
    <p className="text-slate-500 text-xs font-medium mb-0.5">
      No conversations yet
    </p>
    <p className="text-slate-400 text-[11px] mb-3">Start a new thread</p>
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onCreateThread}
      className={cn(
        'group relative px-3 py-1.5 rounded-lg text-xs font-medium overflow-hidden',
        'bg-gradient-to-br from-slate-700 to-slate-800',
        'text-white',
        'shadow-sm shadow-slate-900/20',
        'transition-shadow duration-200',
        'hover:shadow-md hover:shadow-slate-900/25',
        'flex items-center gap-1.5',
      )}
    >
      <div
        className={cn(
          'absolute inset-[1px] rounded-[6px]',
          'bg-gradient-to-br from-white/10 via-transparent to-transparent',
        )}
      />
      <Sparkles className="w-3 h-3 relative z-10" />
      <span className="relative z-10">New Thread</span>
    </motion.button>
  </motion.div>
);

export const ThreadList = ({
  threads,
  selectedThreadId,
  isLoading,
  onSelectThread,
  onCreateThread,
  onDeleteThread,
  onRenameThread,
  onCopyLink,
}: ThreadListProps) => {
  return (
    <div className="h-full flex flex-col bg-slate-50/30">
      {/* Header */}
      <div className="flex-shrink-0 px-3 pt-3 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <h2 className="text-sm font-semibold text-slate-700 tracking-tight">
              Threads
            </h2>
            {threads.length > 0 && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className={cn(
                  'text-[10px] font-medium px-1.5 py-px rounded-full',
                  'bg-slate-200/70 text-slate-500',
                )}
              >
                {threads.length}
              </motion.span>
            )}
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onCreateThread}
            className={cn(
              'group relative w-6 h-6 rounded-lg overflow-hidden',
              'bg-gradient-to-br from-slate-700 to-slate-800',
              'flex items-center justify-center',
              'shadow-sm shadow-slate-900/15',
              'transition-shadow duration-200',
              'hover:shadow-md hover:shadow-slate-900/20',
            )}
          >
            <div
              className={cn(
                'absolute inset-[1px] rounded-[6px]',
                'bg-gradient-to-br from-white/10 via-transparent to-transparent',
              )}
            />
            <Plus
              className="w-3.5 h-3.5 text-white relative z-10"
              strokeWidth={2.5}
            />
          </motion.button>
        </div>

        {/* Subtle divider */}
        <div className="mt-2 h-px bg-gradient-to-r from-transparent via-slate-200/80 to-transparent" />
      </div>

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto px-2 pb-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
        {isLoading ? (
          <LoadingSkeleton />
        ) : threads.length === 0 ? (
          <EmptyState onCreateThread={onCreateThread} />
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-0.5"
          >
            <AnimatePresence mode="popLayout">
              {threads.map((thread) => (
                <motion.div
                  key={thread.id}
                  variants={itemVariants}
                  layout
                  exit={{
                    opacity: 0,
                    x: -20,
                    transition: { duration: 0.15 },
                  }}
                >
                  <ThreadItem
                    id={thread.id}
                    title={thread.title}
                    isSelected={selectedThreadId === thread.id}
                    onSelect={onSelectThread}
                    onDelete={onDeleteThread}
                    onRename={onRenameThread}
                    onCopyLink={onCopyLink}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
};
